import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { useParams, useSearchParams } from 'react-router'
import { basculerLaCase } from './basculer'
import {
  api,
  ApiError,
  type AisleCode,
  type PastShoppingList,
  type ShoppingLine,
  type ShoppingList,
} from '../../lib/api'
import { TabBar } from '../../components/TabBar'
import { AddSheet } from '../items/AddSheet'
import { layout, sousLaBarre } from '../../design/tokens'
import { grouperParRayon, nomDuRayon, RAYONS } from './rayons'
import { invaliderCourses } from '../../lib/cache'
import { BoutonActualiser } from '../../components/BoutonActualiser'

/**
 * Planche 13 du cahier des charges — la liste de courses.
 *
 * Trois exigences de la planche gouvernent cet écran :
 *  - « cocher fait descendre l'article dans "Dans le panier", sans le supprimer » ;
 *  - « le champ d'ajout reste collé au clavier : on saisit 8 articles à la suite sans jamais
 *    quitter l'écran » — le champ garde donc le focus après chaque envoi ;
 *  - « les articles venus d'une recette gardent leur provenance en étiquette ».
 */
const CLE_AFFICHAGE = 'organisateur.courses.parRayon'

/**
 * Le mode d'affichage, retenu sur cet appareil.
 *
 * Par appareil et non par espace : c'est une préférence de lecture, qui dépend d'où l'on est —
 * le téléphone au magasin, le navigateur à la maison. Le stockage peut échouer (fenêtre privée,
 * données bloquées) ; l'écran doit s'ouvrir quand même, sur la liste complète.
 */
function lireAffichage(): boolean {
  try {
    return localStorage.getItem(CLE_AFFICHAGE) === 'oui'
  } catch {
    return false
  }
}

function retenirAffichage(parRayon: boolean) {
  try {
    localStorage.setItem(CLE_AFFICHAGE, parRayon ? 'oui' : 'non')
  } catch {
    // Sans mémoire, le choix vaut pour cette visite : ce n'est pas une raison d'échouer.
  }
}

export function ShoppingScreen() {
  const { spaceId = '' } = useParams()
  const [feuille, setFeuille] = useState(false)
  const [libelle, setLibelle] = useState('')
  const [erreur, setErreur] = useState<string | null>(null)
  const champ = useRef<HTMLInputElement>(null)

  // « ?ajout=1 » : le geste vient du bouton central d'un autre écran. Le champ est déjà là, il
  // suffit d'y mettre le curseur — et le clavier s'ouvre sur un téléphone.
  const [parametres] = useSearchParams()

  useEffect(() => {
    if (parametres.get('ajout') === '1') {
      champ.current?.focus()
    }
  }, [parametres])
  const queryClient = useQueryClient()

  const { data, isPending } = useQuery<ShoppingList>({
    queryKey: ['courses', spaceId],
    queryFn: () => api.shopping(spaceId),
  })

  const rafraichir = () => invaliderCourses(queryClient, spaceId)

  const ajouter = useMutation({
    mutationFn: () => api.addShoppingLine(spaceId, libelle.trim()),
    onSuccess: async () => {
      setLibelle('')
      await rafraichir()
      // Saisie en rafale : on reprend la main sans que le clavier se ferme.
      champ.current?.focus()
    },
    onError: (cause: unknown) =>
      setErreur(cause instanceof ApiError ? cause.message : "L'ajout a échoué."),
  })

  const vider = useMutation({
    mutationFn: () => api.clearBasket(spaceId),
    onSuccess: rafraichir,
  })

  const restants = data?.remaining ?? []
  const panier = data?.inBasket ?? []

  // Deux lectures de la même liste. Par rayon pour faire les courses — on traverse le magasin
  // une fois —, complète pour écrire la liste chez soi, où l'ordre de saisie compte plus.
  const [parRayon, setParRayon] = useState(() => lireAffichage())
  const [historiqueOuvert, setHistoriqueOuvert] = useState(false)

  return (
    <main
      className="mx-auto min-h-dvh bg-surface"
      style={{
        maxWidth: layout.column,
        paddingInline: layout.gutter,
        // La place du champ d'ajout en plus de celle de la barre : sans elle, le dernier
        // article se cachait derrière, ce que montrait la capture.
        paddingBottom: sousLaBarre(76),
      }}
    >
      <header className="flex items-baseline justify-between" style={{ height: layout.header }}>
        <h1 className="font-serif text-screen">Courses</h1>
        <span className="flex items-center gap-3">
          <p className="text-meta text-ink-muted">
            {restants.length} restant{restants.length > 1 ? 's' : ''}
          </p>
          <BoutonActualiser />
        </span>
      </header>

      <div className="mb-3 flex items-center gap-2">
        {[
          { valeur: false, libelle: 'Liste complète' },
          { valeur: true, libelle: 'Par rayon' },
        ].map((choix) => (
          <button
            key={choix.libelle}
            type="button"
            aria-pressed={parRayon === choix.valeur}
            onClick={() => {
              setParRayon(choix.valeur)
              retenirAffichage(choix.valeur)
            }}
            className="rounded-field border px-3 text-meta"
            style={{
              height: 36,
              borderColor: parRayon === choix.valeur ? 'var(--color-accent)' : 'var(--color-border)',
              background:
                parRayon === choix.valeur ? 'var(--color-accent-soft)' : 'var(--color-card)',
              fontWeight: parRayon === choix.valeur ? 600 : 400,
            }}
          >
            {choix.libelle}
          </button>
        ))}

        <button
          type="button"
          onClick={() => setHistoriqueOuvert((ouvert) => !ouvert)}
          className="ml-auto text-meta font-semibold text-accent"
          style={{ height: 36 }}
        >
          {historiqueOuvert ? 'Fermer' : 'Listes passées'}
        </button>
      </div>

      {historiqueOuvert ? (
        <AnciennesListes spaceId={spaceId} onAjoute={rafraichir} />
      ) : null}

      {isPending ? (
        <Squelette />
      ) : (
        <>
          {restants.length === 0 && panier.length === 0 ? (
            <p className="text-body text-ink-muted" style={{ paddingBlock: 16 }}>
              Rien à acheter. Ajoutez un article ci-dessous.
            </p>
          ) : null}

          {parRayon ? (
            grouperParRayon(restants).map((groupe) => (
              <section key={groupe.code ?? 'sans'} className="mb-4">
                <h2 className="mb-2 text-label font-semibold uppercase text-ink-muted">
                  {groupe.nom}
                </h2>
                <ul className="flex flex-col gap-2">
                  {groupe.lignes.map((ligne) => (
                    <Ligne
                      key={ligne.id}
                      spaceId={spaceId}
                      ligne={ligne}
                      montrerLeRayon={false}
                      onDone={rafraichir}
                    />
                  ))}
                </ul>
              </section>
            ))
          ) : (
            <ul className="flex flex-col gap-2">
              {restants.map((ligne) => (
                <Ligne
                  key={ligne.id}
                  spaceId={spaceId}
                  ligne={ligne}
                  montrerLeRayon
                  onDone={rafraichir}
                />
              ))}
            </ul>
          )}

          {panier.length > 0 ? (
            <section className="mt-8">
              <div className="mb-3 flex items-baseline justify-between">
                <h2 className="text-label font-semibold uppercase text-ink-muted">Dans le panier</h2>
                <button
                  type="button"
                  onClick={() => vider.mutate()}
                  className="text-meta font-semibold text-accent"
                >
                  Vider le panier
                </button>
              </div>

              <ul className="flex flex-col gap-2">
                {panier.map((ligne) => (
                  <Ligne
                    key={ligne.id}
                    spaceId={spaceId}
                    ligne={ligne}
                    montrerLeRayon={false}
                    onDone={rafraichir}
                  />
                ))}
              </ul>
            </section>
          ) : null}
        </>
      )}

      {/* Le champ d'ajout est fixé au-dessus de la barre d'onglets : il reste au contact du
          clavier, ce qui est toute la condition de la saisie en rafale.
          Posé sur `sousLaBarre` et non sur la seule hauteur de la barre : depuis qu'elle flotte,
          elle occupe aussi son décollement et la zone sûre, et le champ passait dessous. Et il
          flotte comme elle — une bande pleine largeur posée sur une barre détachée se lirait
          comme une erreur d'alignement. */}
      <form
        // Posé juste au-dessus de la barre, et sans ombre : la barre en porte déjà une, et
        // deux pastilles ombrées l'une sur l'autre faisaient trois surfaces empilées en bas de
        // l'écran — le téléphone en ajoute une quatrième avec sa propre barre (correction S84).
        className="fixed mx-auto flex gap-2 rounded-card border border-border bg-card"
        style={{
          maxWidth: layout.column - layout.tabBarInset * 2,
          left: layout.tabBarInset,
          right: layout.tabBarInset,
          bottom: sousLaBarre(6),
          padding: 8,
        }}
        onSubmit={(event) => {
          event.preventDefault()
          setErreur(null)
          if (libelle.trim().length > 0) {
            ajouter.mutate()
          }
        }}
      >
        <input
          ref={champ}
          value={libelle}
          onChange={(event) => {
            setLibelle(event.target.value)
            setErreur(null)
          }}
          placeholder="Ajouter un article"
          aria-label="Ajouter un article"
          className="grow rounded-field border-[1.5px] border-accent bg-card px-4 outline-none"
          style={{ height: 44, fontSize: 17 }}
        />
        <button
          type="submit"
          disabled={libelle.trim().length === 0 || ajouter.isPending}
          aria-label="Ajouter"
          className="flex items-center justify-center rounded-field bg-accent font-semibold text-card disabled:opacity-40"
          style={{ width: 44, height: 44, fontSize: 22 }}
        >
          +
        </button>
      </form>

      {erreur ? (
        <p
          className="fixed inset-x-0 mx-auto text-meta"
          style={{
            maxWidth: layout.column,
            bottom: sousLaBarre(68),
            paddingInline: 20,
            color: 'var(--color-danger)',
          }}
        >
          {erreur}
        </p>
      ) : null}

      <TabBar onAdd={() => setFeuille(true)} activeSpaceId={spaceId} />

      {feuille ? <AddSheet spaceId={spaceId} onClose={() => setFeuille(false)} /> : null}
    </main>
  )
}

function Ligne({
  spaceId,
  ligne,
  montrerLeRayon,
  onDone,
}: {
  spaceId: string
  ligne: ShoppingLine
  /** Faux dans la vue par rayon : le titre de section le dit déjà. */
  montrerLeRayon: boolean
  onDone: () => Promise<void>
}) {
  const queryClient = useQueryClient()

  const cocher = useMutation({
    mutationFn: () => api.checkShoppingLine(spaceId, ligne.id, !ligne.isChecked),
    // La case change avant que le serveur réponde. Elle attendait deux allers-retours —
    // l'enregistrement, puis le rechargement de la liste — soit un demi-tour du monde pour un
    // geste qu'on répète trente fois dans un magasin (correction S85).
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ['courses', spaceId] })

      const precedent = queryClient.getQueryData<ShoppingList>(['courses', spaceId])

      if (precedent) {
        queryClient.setQueryData<ShoppingList>(
          ['courses', spaceId],
          basculerLaCase(precedent, ligne.id),
        )
      }

      navigator.vibrate?.(10)

      return { precedent }
    },
    // Refusée : on remet ce qui était affiché plutôt que de laisser une case qui ment.
    onError: (_erreur, _variables, contexte) => {
      if (contexte?.precedent) {
        queryClient.setQueryData(['courses', spaceId], contexte.precedent)
      }
    },
    onSettled: onDone,
  })

  const retirer = useMutation({
    mutationFn: () => api.deleteShoppingLine(spaceId, ligne.id),
    onSuccess: onDone,
  })

  const [choixOuvert, setChoixOuvert] = useState(false)

  const classer = useMutation({
    mutationFn: (rayon: AisleCode | '') => api.setShoppingAisle(spaceId, ligne.id, rayon),
    onSuccess: async () => {
      setChoixOuvert(false)
      await onDone()
    },
  })

  return (
    <li
      className="flex items-center gap-3 rounded-card border border-border bg-card px-3"
      style={{ minHeight: layout.minimumTouchTarget, opacity: ligne.isChecked ? 0.72 : 1 }}
    >
      {/* Cible de 48 px : c'est le geste le plus répété de l'écran, en magasin, d'une main. */}
      <button
        type="button"
        role="checkbox"
        aria-checked={ligne.isChecked}
        aria-label={ligne.isChecked ? `Décocher ${ligne.label}` : `Cocher ${ligne.label}`}
        onClick={() => cocher.mutate()}
        className="flex shrink-0 items-center justify-center rounded-field"
        style={{
          width: 28,
          height: 28,
          border: `1.5px solid ${ligne.isChecked ? 'var(--color-accent)' : 'var(--color-border)'}`,
          background: ligne.isChecked ? 'var(--color-accent)' : 'var(--color-card)',
          color: 'var(--color-card)',
          fontSize: 16,
        }}
      >
        {ligne.isChecked ? '✓' : ''}
      </button>

      <div className="grow" style={{ paddingBlock: 6 }}>
        <p
          className="text-card-title font-semibold"
          style={{ textDecoration: ligne.isChecked ? 'line-through' : 'none' }}
        >
          {ligne.label}
          {ligne.quantity ? <span className="text-ink-muted"> {ligne.quantity}</span> : null}
        </p>

        {/* Le rayon, touchable pour en changer — classer doit rester un geste de l'écran où
            l'on est, sinon personne ne corrige jamais une devinette.
            Le mot « changer » n'y figure plus : répété à chaque ligne, il faisait sept fois le
            bruit d'une consigne qu'on ne lit qu'une fois. Le texte lui-même est le bouton.
            Et il disparaît dans la vue par rayon, où le titre de section le dit déjà. */}
        {montrerLeRayon ? (
          <button
            type="button"
            onClick={() => setChoixOuvert((ouvert) => !ouvert)}
            aria-label={`Rayon de ${ligne.label} : ${nomDuRayon(ligne.aisle)}. Changer.`}
            className="text-meta text-ink-muted"
          >
            {nomDuRayon(ligne.aisle)}
          </button>
        ) : null}

        {choixOuvert ? (
          <div className="mt-2 flex flex-wrap gap-2">
            {RAYONS.map((rayon) => (
              <button
                key={rayon.code}
                type="button"
                disabled={classer.isPending}
                onClick={() => classer.mutate(rayon.code)}
                className="rounded-field border px-3 text-meta"
                style={{
                  height: 36,
                  borderColor:
                    ligne.aisle === rayon.code ? 'var(--color-accent)' : 'var(--color-border)',
                  background:
                    ligne.aisle === rayon.code ? 'var(--color-accent-soft)' : 'var(--color-card)',
                }}
              >
                {rayon.nom}
              </button>
            ))}
            {ligne.aisle ? (
              <button
                type="button"
                onClick={() => classer.mutate('')}
                className="text-meta font-semibold text-ink-muted"
                style={{ height: 36 }}
              >
                Déclasser
              </button>
            ) : null}
          </div>
        ) : null}

        {/* « Les articles venus d'une recette gardent leur provenance en étiquette. » */}
        {ligne.sourceItemTitle ? (
          <span
            className="mt-1 inline-block rounded-full px-3 text-meta"
            style={{ background: 'var(--color-accent-soft)', color: 'var(--color-accent)' }}
          >
            {ligne.sourceItemTitle}
          </span>
        ) : null}
      </div>

      <button
        type="button"
        onClick={() => retirer.mutate()}
        aria-label={`Retirer ${ligne.label}`}
        className="shrink-0 text-meta font-semibold text-ink-muted"
        style={{ minWidth: 32 }}
      >
        ✕
      </button>
    </li>
  )
}

/**
 * Les listes déjà faites, et le moyen d'y repêcher ce qu'on rachète.
 *
 * Toucher un article le remet sur la liste du jour. « Tout reprendre » remet la liste entière :
 * les courses de la semaine se ressemblent, et les retaper une à une était le vrai coût.
 */
function AnciennesListes({
  spaceId,
  onAjoute,
}: {
  spaceId: string
  onAjoute: () => Promise<void>
}) {
  const { data, isPending } = useQuery<readonly PastShoppingList[]>({
    queryKey: ['courses-historique', spaceId],
    queryFn: () => api.shoppingHistory(spaceId),
  })

  const [repris, setRepris] = useState<readonly string[]>([])

  const reprendre = useMutation({
    mutationFn: async (lignes: readonly { label: string; quantity: string | null }[]) => {
      for (const article of lignes) {
        await api.addShoppingLine(spaceId, article.label, article.quantity ?? undefined)
      }
    },
    onSuccess: async (_, lignes) => {
      setRepris((actuels) => [...actuels, ...lignes.map((article) => article.label)])
      navigator.vibrate?.(10)
      await onAjoute()
    },
  })

  if (isPending) {
    return <p className="mb-4 text-body text-ink-muted">Chargement…</p>
  }

  if (!data || data.length === 0) {
    return (
      <p className="mb-4 text-body text-ink-muted">
        Aucune liste passée. Elles apparaîtront ici dès que vous aurez vidé un panier.
      </p>
    )
  }

  return (
    <div className="mb-4 flex flex-col gap-3">
      {data.map((liste) => (
        <section key={liste.clearedAt} className="rounded-card border border-border bg-card p-3">
          <div className="flex items-baseline justify-between gap-3">
            <h2 className="text-meta font-semibold text-ink-muted">
              {new Date(liste.clearedAt).toLocaleDateString('fr-FR', {
                weekday: 'long',
                day: 'numeric',
                month: 'long',
              })}
            </h2>
            <button
              type="button"
              disabled={reprendre.isPending}
              onClick={() => reprendre.mutate(liste.lines.map((l) => ({ label: l.label, quantity: l.quantity })))}
              className="shrink-0 text-meta font-semibold text-accent"
            >
              Tout reprendre
            </button>
          </div>

          <div className="mt-2 flex flex-wrap gap-2">
            {liste.lines.map((article) => (
              <button
                key={`${liste.clearedAt}-${article.label}`}
                type="button"
                disabled={reprendre.isPending}
                onClick={() => reprendre.mutate([{ label: article.label, quantity: article.quantity }])}
                className="rounded-field border border-border px-3 text-meta"
                style={{
                  height: 36,
                  // Repris : l'article est déjà sur la liste du jour, le redire n'ajouterait
                  // qu'un doublon.
                  opacity: repris.includes(article.label) ? 0.5 : 1,
                }}
              >
                {repris.includes(article.label) ? '✓ ' : '+ '}
                {article.label}
              </button>
            ))}
          </div>
        </section>
      ))}
    </div>
  )
}

function Squelette() {
  return (
    <ul className="flex flex-col gap-2">
      {[0, 1, 2, 3].map((index) => (
        <li key={index} className="rounded-card border border-border bg-card" style={{ height: 56 }} />
      ))}
    </ul>
  )
}
